import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from '../locales/en.json';
import ka from '../locales/ka.json';

/**
 * UI strings only (§11). Content translations come from the API via
 * spatie/laravel-translatable and are never routed through i18next.
 *
 * The same dictionaries back the admin interface (§26.8) — admin strings will be added
 * under an `admin.*` namespace rather than in a separate file, so "Interface language"
 * (§20.2-13) and the public site stay in sync.
 *
 * NOTE: no language *detector* is wired here on purpose. The locale is owned by the URL
 * (`/` = en, `/ka/...` = ka, §5) and is pushed into i18next by LocaleLayout. Letting a
 * detector also write it would give two sources of truth for the same value.
 */

export const SUPPORTED_LOCALES = ['en', 'ka'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';

export const isLocale = (value: string | undefined): value is Locale =>
  !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);

/** Reads the locale out of a pathname: `/ka/...` → 'ka', anything else → 'en'. */
export const localeFromPath = (pathname: string): Locale => {
  const first = pathname.split('/').filter(Boolean)[0];
  return isLocale(first) ? first : DEFAULT_LOCALE;
};

/**
 * Initialise with the locale ALREADY resolved from the URL, before React renders.
 *
 * Deriving it here rather than letting LocaleLayout set it in an effect fixes two
 * things that a post-render `changeLanguage` cannot:
 *   1. First paint is correct. Otherwise a Georgian deep link renders English chrome
 *      and then flips — visible, and wrong for the prerender/crawler pass in §17.
 *   2. No subscriber can miss the change. Components subscribe to `languageChanged`
 *      from an effect; a language change fired from a parent effect during the same
 *      mount races those subscriptions (React StrictMode's unsubscribe/resubscribe
 *      cycle loses it), leaving the header stale while the page body updates.
 *
 * LocaleLayout still syncs on subsequent client-side navigation, when every component
 * is already mounted and subscribed.
 */
void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    ka: { translation: ka },
  },
  lng: typeof window !== 'undefined' ? localeFromPath(window.location.pathname) : DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
});

export default i18n;
